import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { useTestimonials, addTestimonial, removeTestimonial, toggleVerifyTestimonial } from '../hooks/useTestimonials';
import { useClients, addClient, removeClient, fileToDataUrl } from '../hooks/useClients';
import { useProjects, addProject, removeProject } from '../hooks/useProjects';
import { publishToContentful } from '../lib/contentful';
import { signIn, signOut, getSession, resetPassword, supabase } from '../lib/supabase';
import { ArrowLeft, LayoutGrid, CheckCircle2, AlertCircle, RefreshCw, Lock, LogOut, MessageCircle, Star, Plus, Loader2, ShieldCheck, Users, Menu, X, Upload, Trash2, Image, PenSquare, Briefcase, Mail, KeyRound, ArrowRight } from 'lucide-react';
import { BlogTab } from '../components/admin/BlogTab';
import { DashboardTab } from '../components/admin/DashboardTab';
import { ServicesTab } from '../components/admin/ServicesTab';
// ─── Auth config ───
// Authentication handled by Supabase Auth — supports password reset, change password, sessions
const AUTH_KEY = 'roxie_admin_auth';
const CATEGORIES = ['Brand Identity & Graphic Design', 'Product Design', 'Software & System Development', 'Digital Growth & Strategy'];
function slugify(text: string) {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');
}
function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}
interface FormState {
  title: string;
  slug: string;
  category: string;
  description: string;
  tags: string;
  cover_image: string;
  client_name: string;
  live_url: string;
  featured: boolean;
  published: boolean;
}
const defaultForm: FormState = {
  title: '',
  slug: '',
  category: '',
  description: '',
  tags: '',
  cover_image: '',
  client_name: '',
  live_url: '',
  featured: false,
  published: true
};
function Toggle({
  checked,
  onChange,
  label,
  hint
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return <div className="flex items-center justify-between py-3" data-id="element-827">
      <div data-id="element-828">
        <p className="text-sm font-medium text-gray-700" data-id="element-829">{label}</p>
        {hint && <p className="text-xs text-gray-400 mt-0.5" data-id="element-830">{hint}</p>}
      </div>
      <button type="button" onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${checked ? 'bg-[#00316B]' : 'bg-gray-200'}`} data-id="element-831">
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-832" />
      </button>
    </div>;
}
function Field({
  label,
  hint,
  required,
  children
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return <div className="space-y-1.5" data-id="element-833">
      <label className="block text-sm font-medium text-gray-700" data-id="element-834">
        {label}
        {required && <span className="text-[#FF6600] ml-1" data-id="element-835">*</span>}
      </label>
      {hint && <p className="text-xs text-gray-400" data-id="element-836">{hint}</p>}
      {children}
    </div>;
}
const inputClass = 'w-full px-3.5 py-2.5 text-sm bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00316B]/20 focus:border-[#00316B] transition-colors';
// ─── Login Screen ───
function LoginScreen({
  onLogin
}: {
  onLogin: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shaking, setShaking] = useState(false);
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [resetSent, setResetSent] = useState(false);
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await signIn(email, password);
      if (result.success) {
        sessionStorage.setItem(AUTH_KEY, 'true');
        onLogin();
      } else {
        setError(result.error || 'Invalid email or password');
        setShaking(true);
        setTimeout(() => setShaking(false), 500);
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setShaking(true);
      setTimeout(() => setShaking(false), 500);
    } finally {
      setLoading(false);
    }
  };
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await resetPassword(email);
      if (result.success) {
        setResetSent(true);
      } else {
        setError(result.error || 'Failed to send reset email');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return <main className="min-h-screen bg-[#F8F8F6] flex items-center justify-center px-4" data-id="element-837">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="w-full max-w-sm" data-id="element-838">
        <div className="text-center mb-8" data-id="element-839">
          <div className="w-12 h-12 rounded-xl bg-[#00316B] flex items-center justify-center mx-auto mb-4" data-id="element-840">
            {mode === 'login' ? <Lock className="w-5 h-5 text-white" data-id="element-841" /> : <KeyRound className="w-5 h-5 text-white" data-id="element-842" />}
          </div>
          <h1 className="text-2xl font-bold font-heading text-[#0C0C0C]" data-id="element-843">
            {mode === 'login' ? 'Admin Access' : 'Reset Password'}
          </h1>
          <p className="text-sm text-gray-400 mt-1" data-id="element-844">
            {mode === 'login' ? 'Sign in to manage your site' : 'Enter your email to receive a reset link'}
          </p>
        </div>

        <AnimatePresence mode="wait" data-id="element-845">
          {mode === 'login' ? <motion.form key="login" onSubmit={handleLogin} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-4" data-id="element-846">
              <motion.div animate={shaking ? {
            x: [0, -10, 10, -10, 10, 0]
          } : {}} transition={{
            duration: 0.4
          }} className="space-y-4" data-id="element-847">
                <div className="space-y-1.5" data-id="element-848">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-849">
                    Email
                  </label>
                  <div className="relative" data-id="element-850">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-851" />
                    <input type="email" className={`${inputClass} pl-10`} placeholder="admin@belodigital.com" value={email} onChange={e => {
                  setEmail(e.target.value);
                  setError('');
                }} autoFocus required disabled={loading} data-id="element-852" />
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-853">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-854">
                    Password
                  </label>
                  <input type="password" className={inputClass} placeholder="Enter password" value={password} onChange={e => {
                setPassword(e.target.value);
                setError('');
              }} required disabled={loading} data-id="element-855" />
                </div>

                <AnimatePresence data-id="element-856">
                  {error && <motion.p initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="text-sm text-red-500 flex items-center gap-1.5" data-id="element-857">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-858" />
                      {error}
                    </motion.p>}
                </AnimatePresence>

                <Button type="submit" variant="secondary" className="w-full" isLoading={loading} disabled={loading} data-id="element-859">
                  {loading ? 'Signing in…' : 'Sign In'}
                </Button>
              </motion.div>

              <button type="button" onClick={() => {
            setMode('forgot');
            setError('');
            setResetSent(false);
          }} className="w-full text-center text-xs text-gray-400 hover:text-[#00316B] transition-colors pt-1" data-id="element-860">
                Forgot password?
              </button>
            </motion.form> : <motion.div key="forgot" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-861">
              {resetSent ? <div className="text-center py-4 space-y-4" data-id="element-862">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto" data-id="element-863">
                    <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-864" />
                  </div>
                  <div data-id="element-865">
                    <h3 className="text-lg font-bold text-gray-900" data-id="element-866">
                      Check your email
                    </h3>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-867">
                      We sent a password reset link to{' '}
                      <span className="font-medium text-gray-700" data-id="element-868">{email}</span>
                    </p>
                  </div>
                  <button onClick={() => {
              setMode('login');
              setResetSent(false);
              setError('');
            }} className="inline-flex items-center gap-1.5 text-sm text-[#00316B] font-medium hover:underline" data-id="element-869">
                    <ArrowLeft className="w-3.5 h-3.5" data-id="element-870" />
                    Back to sign in
                  </button>
                </div> : <form onSubmit={handleForgotPassword} className="space-y-4" data-id="element-871">
                  <div className="space-y-1.5" data-id="element-872">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-873">
                      Email
                    </label>
                    <div className="relative" data-id="element-874">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-875" />
                      <input type="email" className={`${inputClass} pl-10`} placeholder="admin@belodigital.com" value={email} onChange={e => {
                  setEmail(e.target.value);
                  setError('');
                }} autoFocus required disabled={loading} data-id="element-876" />
                    </div>
                  </div>

                  <AnimatePresence data-id="element-877">
                    {error && <motion.p initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="text-sm text-red-500 flex items-center gap-1.5" data-id="element-878">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" data-id="element-879" />
                        {error}
                      </motion.p>}
                  </AnimatePresence>

                  <Button type="submit" variant="secondary" className="w-full" isLoading={loading} disabled={loading} leftIcon={<ArrowRight className="w-4 h-4" data-id="element-881" />} data-id="element-880">
                    {loading ? 'Sending…' : 'Send Reset Link'}
                  </Button>

                  <button type="button" onClick={() => {
              setMode('login');
              setError('');
            }} className="w-full text-center text-xs text-gray-400 hover:text-[#00316B] transition-colors pt-1 inline-flex items-center justify-center gap-1" data-id="element-882">
                    <ArrowLeft className="w-3 h-3" data-id="element-883" />
                    Back to sign in
                  </button>
                </form>}
            </motion.div>}
        </AnimatePresence>

        <p className="text-center text-xs text-gray-400 mt-6" data-id="element-884">
          <Link to="/" className="hover:text-gray-600 transition-colors" data-id="element-885">
            ← Back to site
          </Link>
        </p>
      </motion.div>
    </main>;
}
// ─── Testimonials Tab Component ───
function TestimonialsTab() {
  const {
    testimonials,
    loading,
    error,
    refetch,
    toggleVerify
  } = useTestimonials();
  const [form, setForm] = useState({
    name: '',
    company: '',
    quote: '',
    rating: 0
  });
  const [status, setStatus] = useState<'idle' | 'publishing' | 'success'>('idle');
  const [ratingError, setRatingError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    setStatus('publishing');
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'testimonials',
        name: form.name,
        titlerole: form.company,
        testimonial: form.quote,
        rating: form.rating
      });
      // Also save locally
      await addTestimonial(form);
      toast.success('Content Live on CMS!');
      setStatus('success');
      setForm({
        name: '',
        company: '',
        quote: '',
        rating: 0
      });
      setTimeout(() => {
        setStatus('idle');
        setShowForm(false);
      }, 2000);
    } catch (err: any) {
      setStatus('idle');
      toast.error(err?.message || 'Failed to publish. Please try again.');
    }
  };
  const handleDelete = (id: string) => {
    removeTestimonial(id);
    setDeleteConfirm(null);
  };
  return <div className="space-y-6" data-id="element-886">
      {/* Header with New Testimonial button */}
      <div className="flex items-center justify-between" data-id="element-887">
        <div data-id="element-888">
          <h3 className="text-lg font-bold text-[#0C0C0C]" data-id="element-889">
            Testimonials {!loading && `(${testimonials.length})`}
          </h3>
        </div>
        <div className="flex items-center gap-3" data-id="element-890">
          <button onClick={refetch} className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-[#00316B] transition-colors" title="Refresh testimonials" data-id="element-891">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} data-id="element-892" />
            Refresh
          </button>
          <Button variant="secondary" onClick={() => setShowForm(!showForm)} leftIcon={showForm ? <X className="w-4 h-4" data-id="element-894" /> : <Plus className="w-4 h-4" data-id="element-895" />} data-id="element-893">
            {showForm ? 'Cancel' : 'New Testimonial'}
          </Button>
        </div>
      </div>

      {/* Collapsible Form */}
      <AnimatePresence data-id="element-896">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-897">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-id="element-898">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-899">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-900">
                  Add New Testimonial
                </p>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5" data-id="element-901">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-902">
                  <Field label="Name" required data-id="element-903">
                    <input type="text" className={inputClass} placeholder="e.g. Jane Doe" value={form.name} onChange={e => setForm({
                  ...form,
                  name: toTitleCase(e.target.value)
                })} required data-id="element-904" />
                  </Field>
                  <Field label="Company / Organization" required data-id="element-905">
                    <input type="text" className={inputClass} placeholder="e.g. Acme Corp" value={form.company} onChange={e => setForm({
                  ...form,
                  company: toTitleCase(e.target.value)
                })} required data-id="element-906" />
                  </Field>
                </div>

                <Field label="Rating" required data-id="element-907">
                  <div className="flex gap-1" data-id="element-908">
                    {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => {
                  setForm({
                    ...form,
                    rating: star
                  });
                  setRatingError(false);
                }} className="focus:outline-none transition-transform active:scale-90" data-id="element-909">
                        <Star className={`w-6 h-6 ${star <= form.rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-910" />
                      </button>)}
                  </div>
                  {ratingError && <p className="text-xs text-red-500 mt-1" data-id="element-911">
                      Please select a rating
                    </p>}
                </Field>

                <Field label="Testimonial" required data-id="element-912">
                  <textarea className={`${inputClass} resize-none`} rows={3} placeholder="What did they say?" value={form.quote} onChange={e => setForm({
                ...form,
                quote: e.target.value
              })} required data-id="element-913" />
                </Field>

                <div className="flex items-center justify-between pt-2" data-id="element-914">
                  <AnimatePresence data-id="element-915">
                    {status === 'success' && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-green-600 text-sm" data-id="element-916">
                        <CheckCircle2 className="w-4 h-4" data-id="element-917" />
                        Published to CMS!
                      </motion.div>}
                  </AnimatePresence>
                  <Button type="submit" variant="secondary" className="ml-auto" isLoading={status === 'publishing'} disabled={status === 'publishing'} leftIcon={<Plus className="w-4 h-4" data-id="element-919" />} data-id="element-918">
                    {status === 'publishing' ? 'Publishing…' : 'Add Testimonial'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Testimonials List */}
      {loading && <div className="flex items-center justify-center py-12 text-gray-400" data-id="element-920">
          <Loader2 className="w-5 h-5 animate-spin mr-2" data-id="element-921" />
          Loading testimonials…
        </div>}

      {error && <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4" data-id="element-922">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" data-id="element-923" />
          <div data-id="element-924">
            <p className="text-sm font-medium text-red-800" data-id="element-925">
              Failed to load testimonials
            </p>
          </div>
        </div>}

      {!loading && !error && testimonials.length === 0 && <div className="text-center py-12 text-gray-400 text-sm" data-id="element-926">
          No testimonials yet. Click "New Testimonial" to add one!
        </div>}

      <div className="grid grid-cols-1 gap-4" data-id="element-927">
        {testimonials.map(t => <motion.div key={t.id} layout initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className={`bg-white p-4 rounded-xl border shadow-sm flex items-start justify-between group ${t.verified ? 'border-green-200 bg-green-50/20' : 'border-gray-100'}`} data-id="element-928">
            <div className="flex-1 min-w-0" data-id="element-929">
              <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-930">
                <span className="font-bold text-gray-900" data-id="element-931">{t.name}</span>
                <span className="text-gray-400 text-sm" data-id="element-932">•</span>
                <span className="text-gray-500 text-sm" data-id="element-933">{t.company}</span>
                {t.verified && <span className="bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wide flex items-center gap-1" data-id="element-934">
                    <ShieldCheck className="w-3 h-3" data-id="element-935" />
                    Verified
                  </span>}
              </div>
              <div className="flex gap-0.5 mb-2" data-id="element-936">
                {Array.from({
              length: 5
            }).map((_, i) => <Star key={i} className={`w-3 h-3 ${i < t.rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-937" />)}
              </div>
              <p className="text-gray-600 text-sm line-clamp-2" data-id="element-938">"{t.quote}"</p>
            </div>

            <div className="flex flex-col items-end gap-2 ml-4" data-id="element-939">
              <button onClick={() => toggleVerify(t.id)} className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${t.verified ? 'bg-green-100 text-green-700 hover:bg-red-50 hover:text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700'}`} data-id="element-940">
                {t.verified ? 'Unverify' : 'Verify'}
              </button>

              {deleteConfirm === t.id ? <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-941">
                  <button onClick={() => handleDelete(t.id)} className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600" data-id="element-942">
                    Confirm
                  </button>
                  <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-gray-500 text-xs hover:bg-gray-100 rounded" data-id="element-943">
                    Cancel
                  </button>
                </div> : <button onClick={() => setDeleteConfirm(t.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Delete testimonial" data-id="element-944">
                  <Trash2 className="w-4 h-4" data-id="element-945" />
                </button>}
            </div>
          </motion.div>)}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-946">
        Testimonials are published to Contentful CMS.
      </p>
    </div>;
}
// ─── Clients Tab Component ───
function ClientsTab() {
  const {
    clients,
    loading,
    error,
    refetch
  } = useClients();
  const [form, setForm] = useState({
    name: '',
    website: ''
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'publishing' | 'success' | 'error'>('idle');
  const [fileError, setFileError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const MAX_FILE_SIZE = 512 * 1024; // 512KB
  const ACCEPTED_TYPES = ['image/svg+xml', 'image/png'];
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileError('');
    setLogoPreview('');
    setLogoFile(null);
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFileError('Only SVG and PNG files are accepted.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File is too large. Maximum size is 512KB.');
      return;
    }
    setLogoFile(file);
    const dataUrl = await fileToDataUrl(file);
    setLogoPreview(dataUrl);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!logoFile || !logoPreview) {
      setFileError('Please upload a logo file.');
      return;
    }
    setStatus('publishing');
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'clients',
        clientName: form.name,
        websiteUrl: form.website || ''
      });
      // Also save locally
      addClient({
        name: form.name,
        logo: logoPreview
      });
      toast.success('Content Live on CMS!');
      setForm({
        name: '',
        website: ''
      });
      setLogoFile(null);
      setLogoPreview('');
      setStatus('success');
      const fileInput = document.getElementById('client-logo-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      setTimeout(() => {
        setStatus('idle');
        setShowForm(false);
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      toast.error(err?.message || 'Failed to publish. Please try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };
  const handleDelete = (id: string) => {
    removeClient(id);
    setDeleteConfirm(null);
  };
  return <div className="space-y-6" data-id="element-947">
      <div className="flex items-center justify-between" data-id="element-948">
        <div data-id="element-949">
          <h3 className="text-lg font-bold text-[#0C0C0C]" data-id="element-950">
            Clients ({clients.length})
          </h3>
          <p className="text-sm text-gray-400 mt-0.5" data-id="element-951">
            Client logos shown on the homepage trust strip
          </p>
        </div>
        <Button variant="secondary" onClick={() => setShowForm(!showForm)} leftIcon={showForm ? <X className="w-4 h-4" data-id="element-953" /> : <Plus className="w-4 h-4" data-id="element-954" />} data-id="element-952">
          {showForm ? 'Cancel' : 'New Client'}
        </Button>
      </div>

      <AnimatePresence data-id="element-955">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.25
      }} className="overflow-hidden" data-id="element-956">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-id="element-957">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-958">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-959">
                  Add New Client
                </p>
              </div>
              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5" data-id="element-960">
                <Field label="Client Name" required data-id="element-961">
                  <input type="text" className={inputClass} placeholder="e.g. Tenga Finance" value={form.name} onChange={e => setForm({
                ...form,
                name: toTitleCase(e.target.value)
              })} required data-id="element-962" />
                </Field>

                <Field label="Website" hint="Optional — client's website URL" data-id="element-963">
                  <input type="url" className={inputClass} placeholder="https://example.com" value={form.website} onChange={e => setForm({
                ...form,
                website: e.target.value
              })} data-id="element-964" />
                </Field>

                <Field label="Client Logo" required hint="Upload an SVG or PNG file. Recommended dimensions: 200×80px (landscape orientation). Max file size: 512KB." data-id="element-965">
                  <div className="space-y-3" data-id="element-966">
                    <label htmlFor="client-logo-input" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${logoPreview ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 hover:border-[#00316B]/30 hover:bg-[#00316B]/5'}`} data-id="element-967">
                      {logoPreview ? <div className="flex flex-col items-center gap-2" data-id="element-968">
                          <img src={logoPreview} alt="Logo preview" className="h-12 w-auto max-w-[160px] object-contain" data-id="element-969" />
                          <span className="text-xs text-green-600 font-medium" data-id="element-970">
                            {logoFile?.name}
                          </span>
                        </div> : <div className="flex flex-col items-center gap-2 text-gray-400" data-id="element-971">
                          <Upload className="w-6 h-6" data-id="element-972" />
                          <span className="text-sm font-medium" data-id="element-973">
                            Click to upload logo
                          </span>
                          <span className="text-xs" data-id="element-974">SVG or PNG only</span>
                        </div>}
                      <input id="client-logo-input" type="file" accept=".svg,.png,image/svg+xml,image/png" className="hidden" onChange={handleFileChange} data-id="element-975" />
                    </label>

                    {fileError && <p className="text-xs text-red-500 flex items-center gap-1.5" data-id="element-976">
                        <AlertCircle className="w-3.5 h-3.5" data-id="element-977" />
                        {fileError}
                      </p>}

                    <div className="flex items-start gap-2 p-3 bg-blue-50/60 rounded-lg border border-blue-100" data-id="element-978">
                      <Image className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" data-id="element-979" />
                      <div className="text-xs text-blue-700 space-y-1" data-id="element-980">
                        <p className="font-medium" data-id="element-981">Logo guidelines</p>
                        <ul className="list-disc list-inside text-blue-600 space-y-0.5" data-id="element-982">
                          <li data-id="element-983">
                            Recommended size:{' '}
                            <span className="font-medium" data-id="element-984">200×80px</span>{' '}
                            (landscape)
                          </li>
                          <li data-id="element-985">
                            Formats accepted:{' '}
                            <span className="font-medium" data-id="element-986">SVG</span> (preferred)
                            or <span className="font-medium" data-id="element-987">PNG</span>
                          </li>
                          <li data-id="element-988">Use transparent background for best results</li>
                          <li data-id="element-989">Max file size: 512KB</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Field>

                <div className="flex items-center justify-between pt-2" data-id="element-990">
                  <AnimatePresence data-id="element-991">
                    {status === 'success' && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-green-600 text-sm" data-id="element-992">
                        <CheckCircle2 className="w-4 h-4" data-id="element-993" />
                        Client added!
                      </motion.div>}
                    {status === 'error' && <motion.div initial={{
                  opacity: 0,
                  x: -10
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-red-500 text-sm" data-id="element-994">
                        <AlertCircle className="w-4 h-4" data-id="element-995" />
                        Something went wrong
                      </motion.div>}
                  </AnimatePresence>
                  <Button type="submit" variant="secondary" className="ml-auto" isLoading={status === 'publishing'} disabled={status === 'publishing'} leftIcon={<Plus className="w-4 h-4" data-id="element-997" />} data-id="element-996">
                    {status === 'publishing' ? 'Publishing…' : 'Add Client'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {clients.length === 0 && !loading && <div className="text-center py-16 text-gray-400" data-id="element-998">
          <Users className="w-10 h-10 mx-auto mb-3 opacity-40" data-id="element-999" />
          <p className="text-sm font-medium" data-id="element-1000">No clients yet</p>
          <p className="text-xs mt-1" data-id="element-1001">
            Click "New Client" to add your first client logo.
          </p>
        </div>}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-id="element-1002">
        {clients.map(c => <motion.div key={c.id} layout initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center gap-3 group relative" data-id="element-1003">
            <img src={c.logo} alt={c.name} className="h-12 w-auto max-w-[120px] object-contain" onError={e => {
          ;
          (e.target as HTMLImageElement).style.opacity = '0.2';
        }} data-id="element-1004" />
            <span className="text-xs text-gray-600 font-medium text-center truncate w-full" data-id="element-1005">
              {c.name}
            </span>

            {deleteConfirm === c.id ? <div className="absolute inset-0 bg-white/95 rounded-xl flex flex-col items-center justify-center gap-2 p-3" data-id="element-1006">
                <p className="text-xs text-gray-600 text-center" data-id="element-1007">
                  Remove <span className="font-bold" data-id="element-1008">{c.name}</span>?
                </p>
                <div className="flex gap-2" data-id="element-1009">
                  <button onClick={() => handleDelete(c.id)} className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600 transition-colors" data-id="element-1010">
                    Delete
                  </button>
                  <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1011">
                    Cancel
                  </button>
                </div>
              </div> : <button onClick={() => setDeleteConfirm(c.id)} className="absolute top-2 right-2 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all" title="Remove client" data-id="element-1012">
                <Trash2 className="w-3.5 h-3.5" data-id="element-1013" />
              </button>}
          </motion.div>)}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-1014">
        Client data is published to Contentful CMS.
      </p>
    </div>;
}
// ─── Main Admin Page ───
export function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'services' | 'projects' | 'testimonials' | 'clients' | 'blog'>('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const {
    allProjects,
    loading: projectsLoading
  } = useProjects();
  const [form, setForm] = useState<FormState>(defaultForm);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  // Check for existing Supabase session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const session = await getSession();
      if (session) {
        setAuthed(true);
        sessionStorage.setItem(AUTH_KEY, 'true');
      }
      setCheckingSession(false);
    };
    checkAuth();
    // Listen for auth state changes (e.g. password reset redirect)
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setAuthed(true);
        sessionStorage.setItem(AUTH_KEY, 'true');
      }
      if (event === 'SIGNED_OUT') {
        setAuthed(false);
        sessionStorage.removeItem(AUTH_KEY);
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  const handleLogout = async () => {
    await signOut();
    sessionStorage.removeItem(AUTH_KEY);
    setAuthed(false);
  };
  if (checkingSession) {
    return <main className="min-h-screen bg-[#F8F8F6] flex items-center justify-center" data-id="element-1015">
        <Loader2 className="w-6 h-6 animate-spin text-[#00316B]" data-id="element-1016" />
      </main>;
  }
  if (!authed) {
    return <LoginScreen onLogin={() => setAuthed(true)} data-id="element-1017" />;
  }
  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setForm(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === 'title') {
        next.slug = slugify(value);
      }
      return next;
    });
  };
  const handleProjectSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      // Publish to Contentful CMS
      await publishToContentful({
        contentType: 'projects',
        title: form.title,
        slug: form.slug,
        description: form.description,
        clientName: form.client_name,
        liveProjectUrl: form.live_url
      });
      // Also save locally
      addProject({
        title: form.title,
        slug: form.slug,
        category: form.category,
        description: form.description,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        cover_image: form.cover_image,
        client_name: form.client_name,
        live_url: form.live_url,
        featured: form.featured,
        published: form.published
      });
      toast.success('Content Live on CMS!');
      setStatus('success');
      setForm(defaultForm);
      setTimeout(() => {
        setStatus('idle');
        setShowProjectForm(false);
      }, 2000);
    } catch (err: any) {
      setStatus('error');
      toast.error(err?.message || 'Failed to publish. Please try again.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };
  const handleProjectDelete = (id: string) => {
    removeProject(id);
    setDeleteConfirm(null);
  };
  const navItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutGrid
  }, {
    id: 'services',
    label: 'Services',
    icon: Briefcase
  }, {
    id: 'projects',
    label: 'Projects',
    icon: Image
  }, {
    id: 'testimonials',
    label: 'Testimonials',
    icon: MessageCircle
  }, {
    id: 'clients',
    label: 'Clients',
    icon: Users
  }, {
    id: 'blog',
    label: 'Blog',
    icon: PenSquare
  }];
  const getPageTitle = () => {
    switch (activeTab) {
      case 'dashboard':
        return {
          title: 'Dashboard',
          subtitle: 'Overview of your site content'
        };
      case 'services':
        return {
          title: 'Services',
          subtitle: 'Manage your service offerings'
        };
      case 'projects':
        return {
          title: 'Projects',
          subtitle: 'Add and manage portfolio projects'
        };
      case 'testimonials':
        return {
          title: 'Testimonials',
          subtitle: 'Manage client testimonials'
        };
      case 'clients':
        return {
          title: 'Clients',
          subtitle: 'Manage client logos and info'
        };
      case 'blog':
        return {
          title: 'Blog',
          subtitle: 'Write and manage blog posts'
        };
      default:
        return {
          title: 'Dashboard',
          subtitle: 'Manage your content'
        };
    }
  };
  const pageInfo = getPageTitle();
  return <div className="flex min-h-screen bg-[#F8F8F6]" data-id="element-1018">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-1019">
        {isSidebarOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden" data-id="element-1020" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-y-0 left-0 w-64 bg-white z-50 shadow-xl md:hidden flex flex-col" data-id="element-1021">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between" data-id="element-1022">
                <div className="flex items-center gap-3" data-id="element-1023">
                  <img src="https://cdn.magicpatterns.com/uploads/qZq5o2kR7qajKU24kpFCJC/blogo.svg" alt="Belo Digital" className="h-6 w-auto" data-id="element-1024" />
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1" data-id="element-1025">
                    Admin
                  </span>
                </div>
                <button onClick={() => setIsSidebarOpen(false)} className="text-gray-400 hover:text-gray-600" data-id="element-1026">
                  <X className="w-5 h-5" data-id="element-1027" />
                </button>
              </div>
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-1028">
                {navItems.map(item => <button key={item.id} onClick={() => {
              setActiveTab(item.id as any);
              setIsSidebarOpen(false);
            }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id ? 'bg-[#00316B]/10 text-[#00316B]' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-1029">
                    <item.icon className="w-5 h-5" data-id="element-1030" />
                    {item.label}
                  </button>)}
              </nav>
              <div className="p-4 border-t border-gray-100 space-y-2" data-id="element-1031">
                <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1032">
                  <ArrowLeft className="w-5 h-5" data-id="element-1033" />
                  Back to site
                </Link>
                <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-1034">
                  <LogOut className="w-5 h-5" data-id="element-1035" />
                  Sign out
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200 fixed inset-y-0 left-0 z-30" data-id="element-1036">
        <div className="p-6 border-b border-gray-100" data-id="element-1037">
          <div className="flex items-center gap-3" data-id="element-1038">
            <img src="https://cdn.magicpatterns.com/uploads/qZq5o2kR7qajKU24kpFCJC/blogo.svg" alt="Belo Digital" className="h-8 w-auto" data-id="element-1039" />
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-1" data-id="element-1040">
              Admin
            </span>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-id="element-1041">
          {navItems.map(item => <button key={item.id} onClick={() => setActiveTab(item.id as any)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors relative ${activeTab === item.id ? 'bg-[#00316B]/10 text-[#00316B]' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-1042">
              {activeTab === item.id && <div className="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1 bg-[#00316B] rounded-r-full" data-id="element-1043" />}
              <item.icon className="w-5 h-5" data-id="element-1044" />
              {item.label}
            </button>)}
        </nav>

        <div className="p-4 border-t border-gray-100 space-y-2" data-id="element-1045">
          <Link to="/" className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1046">
            <ArrowLeft className="w-5 h-5" data-id="element-1047" />
            Back to site
          </Link>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-id="element-1048">
            <LogOut className="w-5 h-5" data-id="element-1049" />
            Sign out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen md:pl-64 transition-all duration-300" data-id="element-1050">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-8 py-5 flex items-center justify-between sticky top-0 z-20" data-id="element-1051">
          <div className="flex items-center gap-4" data-id="element-1052">
            <button onClick={() => setIsSidebarOpen(true)} className="md:hidden p-2 °ml-2 text-gray-500 hover:bg-gray-100 rounded-lg" data-id="element-1053">
              <Menu className="w-6 h-6" data-id="element-1054" />
            </button>
            <div data-id="element-1055">
              <h1 className="text-2xl font-bold text-[#0C0C0C]" data-id="element-1056">
                {pageInfo.title}
              </h1>
              <p className="text-sm text-gray-500 hidden sm:block" data-id="element-1057">
                {pageInfo.subtitle}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1058">
            <div className="h-8 w-8 rounded-full bg-[#00316B] text-white flex items-center justify-center text-sm font-bold" data-id="element-1059">
              A
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-8 overflow-y-auto" data-id="element-1060">
          <div className="max-w-4xl mx-auto" data-id="element-1061">
            <AnimatePresence mode="wait" data-id="element-1062">
              {activeTab === 'dashboard' ? <motion.div key="dashboard" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1063">
                  <DashboardTab onNavigate={tab => setActiveTab(tab)} data-id="element-1064" />
                </motion.div> : activeTab === 'services' ? <motion.div key="services" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1065">
                  <ServicesTab data-id="element-1066" />
                </motion.div> : activeTab === 'projects' ? <motion.div key="projects" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-1067">
                  <div className="flex items-center justify-between" data-id="element-1068">
                    <div data-id="element-1069">
                      <h3 className="text-lg font-bold text-[#0C0C0C]" data-id="element-1070">
                        Projects ({allProjects.length})
                      </h3>
                    </div>
                    <Button variant="secondary" onClick={() => setShowProjectForm(!showProjectForm)} leftIcon={showProjectForm ? <X className="w-4 h-4" data-id="element-1072" /> : <Plus className="w-4 h-4" data-id="element-1073" />} data-id="element-1071">
                      {showProjectForm ? 'Cancel' : 'New Project'}
                    </Button>
                  </div>

                  <AnimatePresence data-id="element-1074">
                    {showProjectForm && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-1075">
                        <form onSubmit={handleProjectSubmit} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-6" data-id="element-1076">
                          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60" data-id="element-1077">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1078">
                              Project Info
                            </p>
                          </div>
                          <div className="px-6 py-5 space-y-5" data-id="element-1079">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1080">
                              <Field label="Title" required data-id="element-1081">
                                <input type="text" className={inputClass} placeholder="e.g. FinTech Dashboard" value={form.title} onChange={set('title')} required data-id="element-1082" />
                              </Field>
                              <Field label="Slug" hint="Auto-generated from title" data-id="element-1083">
                                <input type="text" className={inputClass} placeholder="e.g. fintech-dashboard" value={form.slug} onChange={set('slug')} data-id="element-1084" />
                              </Field>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1085">
                              <Field label="Category" required data-id="element-1086">
                                <select className={inputClass} value={form.category} onChange={set('category')} required data-id="element-1087">
                                  <option value="" data-id="element-1088">Select a category…</option>
                                  {CATEGORIES.map(c => <option key={c} value={c} data-id="element-1089">
                                      {c}
                                    </option>)}
                                </select>
                              </Field>
                              <Field label="Tags" hint="Comma-separated" data-id="element-1090">
                                <input type="text" className={inputClass} placeholder="UI/UX, Dashboard, Data Viz" value={form.tags} onChange={set('tags')} data-id="element-1091" />
                              </Field>
                            </div>
                            <Field label="Description" required hint="1–2 sentences shown on the project card" data-id="element-1092">
                              <textarea className={`${inputClass} resize-none`} rows={3} placeholder="A brief description of the project…" value={form.description} onChange={set('description')} required data-id="element-1093" />
                            </Field>
                          </div>

                          <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-1094">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1095">
                              Media & Links
                            </p>
                          </div>
                          <div className="px-6 py-5 space-y-5" data-id="element-1096">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1097">
                              <Field label="Cover Image" hint="JPG or PNG, max 2MB" data-id="element-1098">
                                <label htmlFor="project-image-upload" className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${form.cover_image ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-gray-50/50 hover:border-[#00316B]/30 hover:bg-[#00316B]/5'}`} data-id="element-1099">
                                  {form.cover_image ? <div className="flex flex-col items-center gap-2" data-id="element-1100">
                                      <img src={form.cover_image} alt="Upload preview" className="h-16 w-auto max-w-[200px] object-contain rounded" data-id="element-1101" />
                                      <span className="text-xs text-green-600 font-medium" data-id="element-1102">
                                        Click to replace
                                      </span>
                                    </div> : <div className="flex flex-col items-center gap-2 text-gray-400" data-id="element-1103">
                                      <Upload className="w-6 h-6" data-id="element-1104" />
                                      <span className="text-sm font-medium" data-id="element-1105">
                                        Click to upload
                                      </span>
                                      <span className="text-xs" data-id="element-1106">
                                        JPG or PNG
                                      </span>
                                    </div>}
                                  <input id="project-image-upload" type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" className="hidden" onChange={async e => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const ACCEPTED = ['image/jpeg', 'image/png'];
                              if (!ACCEPTED.includes(file.type)) {
                                toast.error('Only JPG and PNG files are accepted.');
                                return;
                              }
                              if (file.size > 2 * 1024 * 1024) {
                                toast.error('File is too large. Maximum size is 2MB.');
                                return;
                              }
                              try {
                                const dataUrl = await fileToDataUrl(file);
                                setForm(prev => ({
                                  ...prev,
                                  cover_image: dataUrl
                                }));
                              } catch {
                                toast.error('Failed to process image.');
                              }
                            }} data-id="element-1107" />
                                </label>
                              </Field>
                              <Field label="Client Name" hint="Optional" data-id="element-1108">
                                <input type="text" className={inputClass} placeholder="e.g. Acme Finance" value={form.client_name} onChange={set('client_name')} data-id="element-1109" />
                              </Field>
                            </div>
                            {form.cover_image && <div className="rounded-lg overflow-hidden border border-gray-100 aspect-[4/3] bg-gray-50 relative group" data-id="element-1110">
                                <img src={form.cover_image} alt="Preview" className="w-full h-full object-cover" onError={e => {
                          ;
                          (e.target as HTMLImageElement).style.display = 'none';
                        }} data-id="element-1111" />
                                <button type="button" onClick={() => {
                          setForm(prev => ({
                            ...prev,
                            cover_image: ''
                          }));
                          const fileInput = document.getElementById('project-image-upload') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }} className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-lg text-gray-500 hover:text-red-500 hover:bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-all" title="Remove image" data-id="element-1112">
                                  <X className="w-4 h-4" data-id="element-1113" />
                                </button>
                              </div>}
                            <Field label="Live URL" hint="Optional — opens when card is clicked" data-id="element-1114">
                              <input type="url" className={inputClass} placeholder="https://..." value={form.live_url} onChange={set('live_url')} data-id="element-1115" />
                            </Field>
                          </div>

                          <div className="px-6 py-4 border-t border-b border-gray-100 bg-gray-50/60" data-id="element-1116">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1117">
                              Visibility
                            </p>
                          </div>
                          <div className="px-6 divide-y divide-gray-100" data-id="element-1118">
                            <Toggle checked={form.published} onChange={v => setForm(p => ({
                        ...p,
                        published: v
                      }))} label="Published" hint="Show this project on the Work page" data-id="element-1119" />
                            <Toggle checked={form.featured} onChange={v => setForm(p => ({
                        ...p,
                        featured: v
                      }))} label="Featured" hint="Show on the homepage preview (max 3)" data-id="element-1120" />
                          </div>

                          <div className="px-6 py-5 border-t border-gray-100 flex items-center justify-between" data-id="element-1121">
                            <AnimatePresence data-id="element-1122">
                              {status === 'success' && <motion.div initial={{
                          opacity: 0,
                          x: -10
                        }} animate={{
                          opacity: 1,
                          x: 0
                        }} exit={{
                          opacity: 0
                        }} className="flex items-center gap-2 text-green-600 text-sm" data-id="element-1123">
                                  <CheckCircle2 className="w-4 h-4" data-id="element-1124" />
                                  Project added!
                                </motion.div>}
                            </AnimatePresence>
                            <div className="flex items-center gap-4 ml-auto" data-id="element-1125">
                              <button type="button" onClick={() => setForm(defaultForm)} className="text-sm text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1126">
                                Clear form
                              </button>
                              <Button type="submit" variant="secondary" isLoading={status === 'loading'} disabled={status === 'loading'} data-id="element-1127">
                                {status === 'loading' ? 'Publishing…' : 'Add Project'}
                              </Button>
                            </div>
                          </div>
                        </form>
                      </motion.div>}
                  </AnimatePresence>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1128">
                    {allProjects.map(p => <motion.div key={p.id} layout initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex gap-4 group" data-id="element-1129">
                        <div className="w-24 h-24 rounded-lg bg-gray-100 overflow-hidden shrink-0" data-id="element-1130">
                          {p.cover_image ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" data-id="element-1131" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-1132">
                              <Image className="w-6 h-6 opacity-50" data-id="element-1133" />
                            </div>}
                        </div>
                        <div className="flex-1 min-w-0 py-1" data-id="element-1134">
                          <div className="flex items-start justify-between gap-2" data-id="element-1135">
                            <h4 className="font-bold text-gray-900 truncate" data-id="element-1136">
                              {p.title}
                            </h4>
                            <div className="flex gap-1 shrink-0" data-id="element-1137">
                              {p.featured && <span className="bg-amber-100 text-amber-700 text-[10px] px-2 py-0.5 rounded font-medium uppercase" data-id="element-1138">
                                  Featured
                                </span>}
                              {!p.published && <span className="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded font-medium uppercase" data-id="element-1139">
                                  Draft
                                </span>}
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1 truncate" data-id="element-1140">
                            {p.category}
                          </p>

                          <div className="mt-auto pt-3 flex items-center justify-end" data-id="element-1141">
                            {deleteConfirm === p.id ? <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-1142">
                                <button onClick={() => handleProjectDelete(p.id)} className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600" data-id="element-1143">
                                  Confirm
                                </button>
                                <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-gray-500 text-xs hover:bg-gray-100 rounded" data-id="element-1144">
                                  Cancel
                                </button>
                              </div> : <button onClick={() => setDeleteConfirm(p.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" title="Delete project" data-id="element-1145">
                                <Trash2 className="w-4 h-4" data-id="element-1146" />
                              </button>}
                          </div>
                        </div>
                      </motion.div>)}
                  </div>

                  {allProjects.length === 0 && !projectsLoading && <div className="text-center py-16 text-gray-400" data-id="element-1147">
                      <LayoutGrid className="w-10 h-10 mx-auto mb-3 opacity-40" data-id="element-1148" />
                      <p className="text-sm font-medium" data-id="element-1149">No projects yet</p>
                      <p className="text-xs mt-1" data-id="element-1150">
                        Click "New Project" to add your first portfolio piece.
                      </p>
                    </div>}

                  <p className="text-center text-xs text-gray-400" data-id="element-1151">
                    Projects are published to Contentful CMS.
                  </p>
                </motion.div> : activeTab === 'testimonials' ? <motion.div key="testimonials" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1152">
                  <TestimonialsTab data-id="element-1153" />
                </motion.div> : activeTab === 'clients' ? <motion.div key="clients" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1154">
                  <ClientsTab data-id="element-1155" />
                </motion.div> : activeTab === 'blog' ? <motion.div key="blog" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} data-id="element-1156">
                  <BlogTab data-id="element-1157" />
                </motion.div> : null}
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>;
}